import buttonStyle from '@/Components/Button/styles.module.scss';
import { selectors, utils } from '@/Store';
import Icon from '@ant-design/icons';
import { BrandType } from '@skytvnz/sky-app-store/lib/types/enums/BrandType';
import { Movie, Show } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { Button } from 'antd';
import classnames from 'classnames';
import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { ReactComponent as Details } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_details.svg';

import { Hero } from '@/Analytics';
import MyListButton from '@/Components/HeroBanner/MyListBtn';
import PlayButton from '@/Components/HeroBanner/PlayButton';
import { PinValidationModalProps } from '@/Components/Modal/usePinValidationModal';
import RecordButton from '@/Components/RecordButton';
import { PackageDetails } from '@/Hooks/PUJ/usePackageDetail';
import { Flags, useFeatureFlag } from '@/Hooks/useFeatureFlags';
import useMedia from '@/Hooks/useMedia';
import { getViewPage } from '@/Utils/PageLocation';
import navigateToPackage from '@/Utils/Puj';
import { useContentSubscriptionCheck } from '@/Hooks/useSubscription';
import useSimpleModal from '../Modal/useSimpleModal';
import { PageType } from './HeroTypes';
import PkgUpgradeBtn from './PkgUpgradeBtn';
import styles from './styles.module.scss';

interface HeroButtonsProps {
  brand: Show | Movie;
  onMyListToggle?: any;
  isHome: boolean;
  hasPkgUpgrade: boolean;
  origin?: string;
  packageDetails?: PackageDetails;
  openPinModal?: (_pkg: PinValidationModalProps) => void;
  indexOnPage: number;
}

const HeroButtons: FC<HeroButtonsProps> = ({
  brand,
  isHome,
  onMyListToggle,
  packageDetails,
  hasPkgUpgrade,
  origin,
  openPinModal,
  indexOnPage = 1,
}) => {
  const {
    isPackageDetailPage = false,
    packageTitle,
    offerId,
    offerText,
    collectionId,
    actualPrice,
  } = packageDetails || {};
  const { isMediaM } = useMedia();
  const history = useHistory();
  const location = useLocation();
  const heroes = useSelector(selectors.home.heroes);
  const { openModal } = useSimpleModal();
  const { hasWatchableHeroButtons } = useContentSubscriptionCheck();
  const isWatchableContent = hasWatchableHeroButtons(brand);
  const isEntitled = isHome || isWatchableContent;

  const brandType = brand.__typename;
  const linkUrl = brandType === BrandType.Show ? 'show' : 'movie';

  const playBtnSetting = utils.hero.getPlayButtonSetting(brand);
  const watchlistBtnSetting = utils.hero.getWatchlistButtonSetting();
  const recordBtnSetting = utils.hero.getRecordButtonSetting(brand);
  const { isOn: isPUJEnabled, isOff: isPUJDisabled } = useFeatureFlag(
    Flags.PACKAGE_UPGRADE_JOURNEY,
  );

  const HeroRecordButton = useCallback(() => {
    const firstAvailableSlot = utils.title.getFirstAvailableSlot(brand);
    const requiredSubscriptions = isWatchableContent
      ? undefined
      : utils.title.getSubscriptions(firstAvailableSlot?.channel);
    const analyticContentTitle = utils.analytics.getSegmentSlotTitleWithBrand(
      firstAvailableSlot,
      utils.title.isSport(brand),
      brand.title,
    );
    return recordBtnSetting.visible ? (
      <RecordButton
        testId="hero-record-button"
        brandTitle={brand.title}
        className={styles.heroButton}
        slot={firstAvailableSlot}
        channel={firstAvailableSlot?.channel || null}
        requiredSubscriptions={requiredSubscriptions}
        isSecordary={playBtnSetting.visible}
        subscriptionAnalyticParams={{
          categoryName: 'Upcoming',
          contentId: firstAvailableSlot?.programme?.id || '',
          contentTitle: analyticContentTitle,
          contentType: utils.analytics.getContentTypeByBrand(brand),
          viewName: getViewPage(history),
          channelName: utils.analytics.getSegmentSlotChannel(firstAvailableSlot?.channel),
        }}
        remoteRecordAnalyticParams={{
          categoryName: 'Upcoming',
          contentTitle: analyticContentTitle,
          history,
          genres: utils.analytics.getSegmentBrandGenres(brand),
          contentType: utils.analytics.getContentTypeByBrand(brand),
          channelName: firstAvailableSlot?.channel?.title,
        }}
      />
    ) : null;
  }, [brand, playBtnSetting, recordBtnSetting, history, isWatchableContent]);

  const HomePageButtons = useCallback(() => {
    const onDetailBtnClick = () => {
      Hero.recordDetailsButtonClick(
        brand.id,
        brand.title,
        utils.analytics.getContentTypeByBrand(brand) || '',
        PageType.Home,
        heroes?.map(h => h.id),
        indexOnPage,
      );
    };
    return isMediaM ? (
      <div className={styles.buttonRow}>
        {playBtnSetting.visible && (
          <PlayButton
            brand={brand}
            isEntitled={isEntitled}
            isHome
            isVod={playBtnSetting.isVod}
            videoContent={playBtnSetting.videoContent}
          />
        )}
        <HeroRecordButton />
        <Link to={`/${linkUrl}/${brand.id}`} onClick={onDetailBtnClick}>
          <Button
            type="primary"
            className={classnames(styles.heroButton, styles.borderlessButton)}
            data-testid="hero-banner-play-details-button"
            name="Hero banner details button"
          >
            <Icon className={styles.heroButtonIcon} component={Details as React.FC} />
            <span className={styles.heroButtonText}>DETAILS</span>
          </Button>
        </Link>
      </div>
    ) : (
      <Link
        to={`/${linkUrl}/${brand.id}`}
        className={styles.buttonMask}
        onClick={onDetailBtnClick}
      />
    );
  }, [isMediaM, linkUrl, brand, isEntitled, playBtnSetting, heroes, indexOnPage]);

  const DetailsPageButtons = useCallback(() => {
    const getPackage = `Add ${offerText}`;
    const learnMore = `More About ${packageTitle}`;

    const onWatch = () => {
      openPinModal?.({
        offerId: offerId ?? '',
        packageName: packageTitle ?? '',
        packagePrice: actualPrice ?? '',
        collectionId: collectionId ?? '',
        origin,
      });
    };
    const onNavigatePackageScreen = () => {
      navigateToPackage({
        offerId,
        history,
        location,
        openModal,
      });
    };
    let buttons: any = null;

    if (hasPkgUpgrade && isPUJEnabled) {
      buttons = offerId ? (
        <div className={styles.buttonsContainer}>
          <PkgUpgradeBtn btnText={getPackage} onClick={onWatch} />
          {!isPackageDetailPage && (
            <Button
              type="primary"
              className={styles.secondary}
              onClick={onNavigatePackageScreen}
              name="Learn more button"
            >
              <span className={buttonStyle.controlButtonSpan}>{learnMore}</span>
            </Button>
          )}
        </div>
      ) : (
        <>
          {isMediaM && (
            <div className={styles.buttonRow}>
              {watchlistBtnSetting.visible && onMyListToggle && (
                <MyListButton onMyList={brand.onMyList} onToggle={onMyListToggle} />
              )}
            </div>
          )}
        </>
      );
    } else if (isMediaM) {
      buttons = (
        <div className={styles.buttonRow}>
          {playBtnSetting.visible && (isPUJDisabled || isWatchableContent) && (
            <PlayButton
              brand={brand}
              isEntitled={isEntitled}
              isHome={false}
              isVod={playBtnSetting.isVod}
              videoContent={playBtnSetting.videoContent}
            />
          )}
          <HeroRecordButton />
          {watchlistBtnSetting.visible && onMyListToggle && (
            <MyListButton onMyList={brand.onMyList} onToggle={onMyListToggle} />
          )}
        </div>
      );
    } else if (playBtnSetting.visible && (isPUJDisabled || isWatchableContent)) {
      buttons = (
        <PlayButton
          brand={brand}
          isEntitled={isEntitled}
          isHome={false}
          isIconOnly
          isVod={playBtnSetting.isVod}
          videoContent={playBtnSetting.videoContent}
        />
      );
    }
    return buttons;
  }, [
    isMediaM,
    isEntitled,
    hasPkgUpgrade,
    isWatchableContent,
    onMyListToggle,
    playBtnSetting,
    watchlistBtnSetting,
    brand,
    history,
    offerId,
    packageTitle,
    origin,
    openModal,
    location,
    collectionId,
    isPackageDetailPage,
    offerText,
    openPinModal,
    isPUJEnabled,
    isPUJDisabled,
    actualPrice,
  ]);

  return isHome ? <HomePageButtons /> : <DetailsPageButtons />;
};

export default HeroButtons;
